/**
 * Every copy decision the Quote Activity log and the Baseline lock banner make, as pure
 * functions. Kept out of the components so every permutation can be unit-tested without
 * rendering anything.
 */

/** Sentinel the caller replaces with the localised "Deleted user" label. */
export const DELETED_USER = '__DELETED_USER__';

export type QuoteActivityEntry = {
	eventType: string | null;
	personName: string | null;
	clientContactName: string | null;
	reason: string | null;
};

/**
 * One rendered log entry: a bold event title, the acting user, and — only where the event can
 * carry one — the contact the decision was recorded for.
 */
export type ActivityCopy = {
	titleMessageId: string;
	person: string;
	contact: string | null;
};

/**
 * The event title, and whether that event can name a contact. PUBLISHED never can — a publish is
 * not made on anyone's behalf — so its contact is dropped even if the row somehow carries one.
 */
const EVENT_TITLES: {[eventType: string]: {titleMessageId: string; carriesContact: boolean}} = {
	PUBLISHED: {titleMessageId: 'quote_activity.entry.published', carriesContact: false},
	APPROVED: {titleMessageId: 'quote_activity.entry.approved', carriesContact: true},
	DECLINED: {titleMessageId: 'quote_activity.entry.declined', carriesContact: true},
};

/**
 * Every status the Baseline stays locked in. REVISED is deliberately absent — revising a quote
 * unlocks the Baseline again. Mirrors the constant InitialPlanPage derives its lock from.
 */
export const LOCKED_QUOTE_STATUSES: readonly string[] = ['PUBLISHED', 'SENT', 'APPROVED', 'DECLINED'];

const BANNER_KEYS: {[status: string]: string} = {
	PUBLISHED: 'baseline.locked_banner_published',
	SENT: 'baseline.locked_banner_sent',
	APPROVED: 'baseline.locked_banner_approved',
	DECLINED: 'baseline.locked_banner_declined',
};

/**
 * The three contact states, told apart by the snapshot column alone:
 *   id set + name set   → the contact exists          → "On behalf of Jane Doe"
 *   id null + name set  → deleted after the decision   → the SAME line, from the snapshot
 *   id null + name null → no contact was ever recorded → no on-behalf-of line at all
 *
 * The first two are indistinguishable here on purpose: only the name matters for display, which
 * is why this takes clientContactName and not the id.
 *
 * Returns null for an unknown event type rather than rendering a broken entry: later lifecycle
 * work adds event types (expiry, revision) that this build does not know about.
 */
export function describeActivity(entry: QuoteActivityEntry): ActivityCopy | null {
	if (!entry.eventType) return null;
	const event = EVENT_TITLES[entry.eventType];
	if (!event) return null;

	const person = entry.personName && entry.personName.trim() ? entry.personName : DELETED_USER;
	const snapshot = entry.clientContactName && entry.clientContactName.trim() ? entry.clientContactName : null;

	return {
		titleMessageId: event.titleMessageId,
		person,
		contact: event.carriesContact ? snapshot : null,
	};
}

export function bannerMessageId(status: string | null): string | null {
	if (!status) return null;
	return BANNER_KEYS[status] || null;
}
